/**
 * Supabase Client Configuration
 *
 * The SDK is loaded only when account or database functionality is first used,
 * keeping it out of the app's first-paint JavaScript bundle.
 */

const supabaseUrl = String(process.env.REACT_APP_SUPABASE_URL || '').trim();
const supabaseAnonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();

const configured = Boolean(supabaseUrl && supabaseAnonKey);
let clientPromise = null;

if (!configured) {
  console.warn('Missing Supabase environment variables. Database features will be disabled.');
}

/**
 * Resolve the real Supabase client on demand. Reusing the same promise prevents
 * duplicate clients when account restoration and its auth listener start at
 * the same time.
 */
export async function getSupabaseClient() {
  if (!configured) return null;
  if (!clientPromise) {
    clientPromise = import('@supabase/supabase-js').then(({ createClient }) =>
      createClient(supabaseUrl, supabaseAnonKey)
    );
  }
  return clientPromise;
}

const callAuth = (method) => async (...args) => {
  const client = await getSupabaseClient();
  if (!client) throw new Error('Supabase is not configured.');
  return client.auth[method](...args);
};

function onAuthStateChange(callback) {
  let cancelled = false;
  let activeSubscription = null;

  getSupabaseClient()
    .then((client) => {
      if (!client || cancelled) return;
      const { data } = client.auth.onAuthStateChange(callback);
      activeSubscription = data && data.subscription ? data.subscription : null;
      if (cancelled && activeSubscription) activeSubscription.unsubscribe();
    })
    .catch(() => {
      // Account helpers already degrade gracefully when the SDK cannot load.
    });

  return {
    data: {
      subscription: {
        unsubscribe() {
          cancelled = true;
          if (activeSubscription) activeSubscription.unsubscribe();
        },
      },
    },
  };
}

/**
 * Compatibility facade for the account service. Auth calls keep their existing
 * shape while the much larger SDK becomes an asynchronous chunk.
 */
export const supabase = configured
  ? {
      auth: {
        getUser: callAuth('getUser'),
        onAuthStateChange,
        signInWithOtp: callAuth('signInWithOtp'),
        verifyOtp: callAuth('verifyOtp'),
        signOut: callAuth('signOut'),
      },
    }
  : null;

/**
 * Check if Supabase is configured and available
 */
export const isSupabaseConfigured = () => {
  return configured;
};

// =============================================================================
// DATABASE HELPER FUNCTIONS
// =============================================================================

/**
 * User management helpers
 */
export const userDb = {
  /**
   * Get user by ID or create if not exists
   * @param {string} userId
   * @returns {Promise<Object|null>}
   */
  async getOrCreateUser(userId) {
    const client = await getSupabaseClient();
    if (!client) return null;

    const { data, error } = await client
      .from('users')
      .upsert({ id: userId, app: 'beloved' }, { onConflict: 'id' })
      .select()
      .single();

    if (error) {
      console.error('Error getting/creating user:', error);
      return null;
    }
    return data;
  },

  /**
   * Update user streak based on last activity
   * @param {string} userId
   * @returns {Promise<number>}
   */
  async updateStreak(userId) {
    const client = await getSupabaseClient();
    if (!client) return 1;

    const today = new Date().toISOString().split('T')[0];

    const { data: user } = await client
      .from('users')
      .select('last_active, streak_count')
      .eq('id', userId)
      .single();

    let newStreak = 1;
    if (user?.last_active) {
      const lastActive = new Date(user.last_active);
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const lastActiveDate = lastActive.toISOString().split('T')[0];
      const yesterdayDate = yesterday.toISOString().split('T')[0];

      if (lastActiveDate === yesterdayDate) {
        newStreak = (user.streak_count || 0) + 1;
      } else if (lastActiveDate === today) {
        newStreak = user.streak_count || 1;
      }
    }

    await client
      .from('users')
      .update({ last_active: today, streak_count: newStreak })
      .eq('id', userId);

    return newStreak;
  },

  /**
   * Update user preferences
   * @param {string} userId
   * @param {Object} preferences
   */
  async updatePreferences(userId, preferences) {
    const client = await getSupabaseClient();
    if (!client) return;

    await client
      .from('users')
      .update({ preferences })
      .eq('id', userId);
  },
};

/**
 * Gratitude entry helpers
 */
export const gratitudeDb = {
  /**
   * Save a gratitude entry
   * @param {string} userId
   * @param {Object} entry
   * @returns {Promise<Object|null>}
   */
  async saveEntry(userId, entry) {
    const client = await getSupabaseClient();
    if (!client) return null;

    const { data, error } = await client
      .from('beloved_gratitude_entries')
      .insert({ user_id: userId, ...entry })
      .select()
      .single();

    if (error) {
      console.error('Error saving gratitude entry:', error);
      return null;
    }
    return data;
  },

  /**
   * Get all gratitude entries for a user
   * @param {string} userId
   * @param {number} limit
   * @returns {Promise<Array>}
   */
  async getEntries(userId, limit = 50) {
    const client = await getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
      .from('beloved_gratitude_entries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching gratitude entries:', error);
      return [];
    }
    return data || [];
  },

  /**
   * Delete a gratitude entry
   * @param {string} entryId
   * @returns {Promise<boolean>}
   */
  async deleteEntry(entryId) {
    const client = await getSupabaseClient();
    if (!client) return false;

    const { error } = await client
      .from('beloved_gratitude_entries')
      .delete()
      .eq('id', entryId);

    return !error;
  },
};

/**
 * Saved prayers helpers
 */
export const prayersDb = {
  /**
   * Save a prayer
   * @param {string} userId
   * @param {Object} prayer
   * @returns {Promise<Object|null>}
   */
  async savePrayer(userId, prayer) {
    const client = await getSupabaseClient();
    if (!client) return null;

    const { data, error } = await client
      .from('beloved_saved_prayers')
      .insert({ user_id: userId, ...prayer })
      .select()
      .single();

    if (error) {
      console.error('Error saving prayer:', error);
      return null;
    }
    return data;
  },

  /**
   * Get saved prayers for a user
   * @param {string} userId
   * @returns {Promise<Array>}
   */
  async getSavedPrayers(userId) {
    const client = await getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
      .from('beloved_saved_prayers')
      .select('*')
      .eq('user_id', userId)
      .order('saved_at', { ascending: false });

    if (error) {
      console.error('Error fetching saved prayers:', error);
      return [];
    }
    return data || [];
  },

  /**
   * Remove a saved prayer
   * @param {string} prayerId
   * @returns {Promise<boolean>}
   */
  async removePrayer(prayerId) {
    const client = await getSupabaseClient();
    if (!client) return false;

    const { error } = await client
      .from('beloved_saved_prayers')
      .delete()
      .eq('id', prayerId);

    return !error;
  },
};

/**
 * Prayer intentions helpers
 */
export const intentionsDb = {
  /**
   * Save a prayer intention
   * @param {string} userId
   * @param {string} intentionText
   * @returns {Promise<Object|null>}
   */
  async saveIntention(userId, intentionText) {
    const client = await getSupabaseClient();
    if (!client) return null;

    const { data, error } = await client
      .from('beloved_intentions')
      .insert({ user_id: userId, intention_text: intentionText })
      .select()
      .single();

    if (error) {
      console.error('Error saving intention:', error);
      return null;
    }
    return data;
  },

  /**
   * Get all intentions for a user
   * @param {string} userId
   * @returns {Promise<Array>}
   */
  async getIntentions(userId) {
    const client = await getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
      .from('beloved_intentions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching intentions:', error);
      return [];
    }
    return data || [];
  },

  /**
   * Mark an intention as answered
   * @param {string} intentionId
   * @returns {Promise<boolean>}
   */
  async markAnswered(intentionId) {
    const client = await getSupabaseClient();
    if (!client) return false;

    const { error } = await client
      .from('beloved_intentions')
      .update({
        answered: true,
        answered_at: new Date().toISOString(),
      })
      .eq('id', intentionId);

    return !error;
  },

  /**
   * Delete an intention
   * @param {string} intentionId
   * @returns {Promise<boolean>}
   */
  async deleteIntention(intentionId) {
    const client = await getSupabaseClient();
    if (!client) return false;

    const { error } = await client
      .from('beloved_intentions')
      .delete()
      .eq('id', intentionId);

    return !error;
  },
};

/**
 * AI conversation helpers
 */
export const aiDb = {
  /**
   * Save an AI conversation exchange
   * @param {string} userId
   * @param {Object} conversation
   */
  async saveConversation(userId, conversation) {
    const client = await getSupabaseClient();
    if (!client) return;

    await client
      .from('ai_conversations')
      .insert({
        user_id: userId,
        app: 'beloved',
        ...conversation,
      });
  },

  /**
   * Get recent AI conversations for context
   * @param {string} userId
   * @param {string} feature
   * @param {number} limit
   * @returns {Promise<Array>}
   */
  async getRecentConversations(userId, feature, limit = 5) {
    const client = await getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
      .from('ai_conversations')
      .select('*')
      .eq('user_id', userId)
      .eq('feature', feature)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching AI conversations:', error);
      return [];
    }
    return data || [];
  },
};

/**
 * Static data helpers (prayers library)
 */
export const staticDb = {
  /**
   * Get all prayers from the library
   * @param {string} category - Optional category filter
   * @returns {Promise<Array>}
   */
  async getPrayers(category = null) {
    const client = await getSupabaseClient();
    if (!client) return [];

    let query = client.from('beloved_prayers').select('*');

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query.order('title');

    if (error) {
      console.error('Error fetching prayers:', error);
      return [];
    }
    return data || [];
  },
};

export default supabase;
